import { apiFetcher } from '@/api/api-fetcher'
import { BATCH_CONFIG } from '@/config/batch'
import { logger } from '@/utils/logger'
import type { AnalysisResponse } from '@/types/api/responses'
import { type BatchExecutionStatus } from '@/types/analysis/batch-status'
import type { AIModelProvider, AIModelVersion } from '@/types/ai-models'

// バッチ分析リクエストの型定義
export interface BatchAnalysisRequest {
  // バッチ識別
  concurrentExecutionId: string
  questions: string[] // 1-100件
  models: Array<{
    provider: AIModelProvider
    version: AIModelVersion | null
  }> // 1-5件

  // 既存機能との整合性
  serviceId: string // serviceIdを必須に
  serviceName: string
  monitoringDomains?: string[]
  competitorIds?: string[]
  context?: string
  initialTags?: string[]
  promptIds?: string[] // promptIdsを追加
  questionMappings?: Array<{
    questionText: string
    questionId: string
    metadata?: {
      keyword?: string
      nominationType?: string
      searchIntentType?: string
    }
  }> // 各質問のメタデータマッピング

  // Notification settings removed - using WebSocket-based approach

  // バッチ特有の設定
  executionConfig?: {
    mode: 'parallel' | 'sequential'
    maxConcurrency?: number // デフォルト: 4
    timeoutSeconds?: number // デフォルト: 900
  }
  connectionPoolConfig?: {
    mode?: 'aggressive' | 'balanced' | 'conservative'
    maxPoolUsagePercent?: number
  }
}

// バッチ開始レスポンスの型定義
export interface BatchStartResponse {
  success: true
  data: {
    concurrentExecutionId: string
    status: BatchExecutionStatus
    progress: {
      completed: number
      total: number
      percentage: number
      failed: number
      pending: number

      // 🆕 分析単位のカウンター（フロントエンド表示用）
      analysis?: {
        completed: number
        total: number
        failed: number
        percentage: number
        pending: number
      }
    }
    executionDetails: Array<{
      questionIndex: number
      questionText: string
      modelProvider: string
      modelVersion: string | null
      status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED'
      startedAt?: string
      completedAt?: string
      error?: string
      executionTimeMs?: number
    }>
    startedAt: string // ISO 8601
    analysisMetrics?: {
      totalAnalysesCount: number
      completedAnalysesCount: number
      failedAnalysesCount: number
      analysisProgressPercentage: number
    }
  }
}

export interface BatchProgressBulkResponse {
  success: true
  data: Array<{
    batchId: string
    status: BatchExecutionStatus
    progress: {
      completed: number
      total: number
      percentage: number
      failed: number
      pending: number
    }
    analysisMetrics?: {
      totalAnalysesCount: number
      completedAnalysesCount: number
      failedAnalysesCount: number
      analysisProgressPercentage: number
    }
  }>
}

// バッチ分析結果の型定義
export interface BatchResultsResponse {
  success: true
  data: {
    concurrentExecutionId: string
    status: 'SUCCEEDED' | 'FAILED'
    results: AnalysisResponse[]
    summary: {
      totalRequested: number
      completed: number
      failed: number
      successRate: number
    }
    executionSummary: {
      startedAt: string // ISO 8601
      completedAt: string // ISO 8601
      totalExecutionTimeMs: number
      averageExecutionTimeMs: number
    }
    failedExecutions?: Array<{
      questionIndex: number
      questionText: string
      modelProvider: string
      modelVersion: string | null
      error: string
      errorCode?: string
    }>
    // WHY: parentBatchId is redundant; retry flags are enough for UI.
    isRetryBatch?: boolean
    retryType?: string
  }
}

// バッチ結果APIレスポンス構造の型定義
export interface BatchResultsApiResponse {
  batchSummary: {
    status?: 'SUCCEEDED' | 'FAILED'
    totalRequested?: number
    completed?: number
    failed?: number
    successRate?: number
    startedAt?: string
    completedAt?: string
    totalExecutionTimeMs?: number
    averageExecutionTimeMs?: number
    failedExecutions?: Array<{
      questionIndex: number
      questionText: string
      modelProvider: string
      modelVersion: string | null
      error: string
      errorCode?: string
    }>
  }
  resultsByQuestion:
    | Array<{
        questionIndex?: number
        questionText?: string
        results?: AnalysisResponse[]
      }>
    | Record<string, AnalysisResponse[] | { results?: AnalysisResponse[] }>
}

// エラーレスポンスの型定義
export interface BatchErrorResponse {
  success: false
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
  }
}

// BatchExecutionStatus type is now imported from @/types/analysis/batch-status
// Update AnalysisResult status to include cancelled
export interface AnalysisResult {
  model: string
  analysisId: string
  status: 'SUCCEEDED' | 'FAILED'
  scores?: Record<
    string,
    {
      value: number
      category: string
      higherIsBetter: boolean
      displayName: string
      japaneseDisplayName?: string | null
    }
  >
  summary?: string
  executionTimeMs: number
  completedAt?: string
  failedAt?: string
  error?: string
}

// Update ModelResults to include cancelled status
export interface ModelResults {
  modelProvider: string
  modelVersion: string | null
  results: Array<{
    questionIndex: number
    question: string
    analysisId: string
    status: 'SUCCEEDED' | 'FAILED'
    scores?: Record<
      string,
      {
        value: number
        category: string
        higherIsBetter: boolean
        displayName: string
        japaneseDisplayName?: string | null
      }
    >
    summary?: string
    executionTimeMs: number
    completedAt?: string
    failedAt?: string
    error?: string
  }>
}

/**
 * バッチ分析を開始する
 * @param spaceId スペースID
 * @param serviceId サービスID
 * @param request バッチ分析リクエスト
 * @param signal AbortSignal
 * @returns バッチ分析開始レスポンス
 */
export async function startBatchAnalysis(
  spaceId: string,
  serviceId: string,
  request: BatchAnalysisRequest,
  signal?: AbortSignal
): Promise<BatchStartResponse> {
  logger.debug('[BatchAPI] Starting batch analysis:', {
    spaceId,
    serviceId,
    concurrentExecutionId: request.concurrentExecutionId,
    questionCount: request.questions.length,
    modelCount: request.models.length,
    totalCombinations: request.questions.length * request.models.length,
  })

  // 🔍 [DEBUG] Consoleでリクエストデータの詳細をログ（Cloud Logging構造化形式）
  console.warn('🔍 [BatchAPI] Sending batch analysis request with questionMappings', {
    severity: 'WARNING',
    component: 'BatchAnalysisAPI',
    operation: 'start_batch_analysis',
    phase: 'request_preparation',
    space_id: spaceId,
    service_id: serviceId,
    concurrent_execution_id: request.concurrentExecutionId,
    metrics: {
      questions_count: request.questions.length,
      models_count: request.models.length,
      total_analyses: request.questions.length * request.models.length,
      question_mappings_count: request.questionMappings?.length || 0,
      has_question_mappings: !!request.questionMappings,
    },
    context: {
      questionMappings_sample: request.questionMappings?.[0],
      has_prefixed_metadata:
        request.questionMappings?.some(
          qm =>
            qm.metadata &&
            Object.values(qm.metadata).some(
              val =>
                typeof val === 'string' &&
                (val.startsWith('kw: ') ||
                  val.startsWith('nomination_type: ') ||
                  val.startsWith('intent_type: '))
            )
        ) || false,
      request_keys: Object.keys(request),
      full_request: request,
    },
    timestamp: new Date().toISOString(),
  })

  try {
    // 応答待ちの上限（D4）。超過しても成功として扱わない（D2）
    const timeoutSignal = AbortSignal.timeout(BATCH_CONFIG.startRequestTimeoutMs)
    const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal

    type LegacyStartResponse = { results?: { concurrentExecutionId?: string } } & Record<
      string,
      unknown
    >
    type StartBatchApiResponse = BatchStartResponse | LegacyStartResponse
    const apiResponse = await apiFetcher.post<StartBatchApiResponse>(
      `/spaces/${spaceId}/services/${serviceId}/analyses/batch`,
      request,
      { signal: combinedSignal }
    )

    // レスポンス構造の確認

    // 🔧 BatchStartResponse形式に変換
    let normalizedResponse: BatchStartResponse

    // Case 1: 既にBatchStartResponse形式の場合（progressフィールドが存在する）
    if (
      'success' in apiResponse &&
      apiResponse.success &&
      'data' in apiResponse &&
      apiResponse.data &&
      typeof apiResponse.data === 'object' &&
      'status' in apiResponse.data &&
      'progress' in apiResponse.data
    ) {
      normalizedResponse = apiResponse as BatchStartResponse
      logger.debug('[BatchAPI] レスポンスは既にBatchStartResponse形式です')
    }
    // Case 2: 従来の形式の場合（message, results等を含む）
    else {
      logger.debug('[BatchAPI] レスポンスをBatchStartResponse形式に変換します')

      const totalCombinations = request.questions.length * request.models.length

      // 🔧 変換処理
      normalizedResponse = {
        success: true,
        data: {
          concurrentExecutionId: request.concurrentExecutionId,
          status: 'PENDING', // 開始直後は PENDING
          progress: {
            completed: 0,
            total: totalCombinations,
            percentage: 0,
            failed: 0,
            pending: totalCombinations,
          },
          executionDetails: request.questions.flatMap((question, questionIndex) =>
            request.models.map((model, _modelIndex) => ({
              questionIndex,
              questionText: question,
              modelProvider: model.provider,
              modelVersion: model.version,
              status: 'PENDING' as const,
            }))
          ),
          startedAt: new Date().toISOString(),
        },
      }

      // 実際のレスポンスにconcurrentExecutionIdがある場合は使用
      if (
        typeof apiResponse === 'object' &&
        apiResponse !== null &&
        'results' in apiResponse &&
        typeof apiResponse.results === 'object' &&
        apiResponse.results !== null &&
        'concurrentExecutionId' in apiResponse.results &&
        typeof apiResponse.results.concurrentExecutionId === 'string'
      ) {
        normalizedResponse.data.concurrentExecutionId = apiResponse.results.concurrentExecutionId
      }
    }

    logger.debug('[BatchAPI] Batch analysis started:', {
      concurrentExecutionId: normalizedResponse.data.concurrentExecutionId,
      status: normalizedResponse.data.status,
      total: normalizedResponse.data.progress.total,
    })

    return normalizedResponse
  } catch (error) {
    // 応答待ちの上限に達した場合。受理されたかどうかは確定していないため、
    // 「タイムアウトした」という事実のまま呼び出し元へ伝える（D2）。
    // 受理されていない実行に対する擬似的な実行IDも払い出さない。
    const isTimeout = error instanceof Error && error.name === 'TimeoutError'

    // サーバーエラーレスポンスの詳細を抽出
    let errorMessage = 'Unknown error occurred'
    let errorDetails: Record<string, unknown> | undefined
    let httpStatus: number | undefined

    if (error instanceof Error) {
      // api-fetcherが既にエラーメッセージを抽出してerror.messageに設定している
      errorMessage = error.message

      // ApiError型の場合、追加の詳細情報を取得
      const apiError = error as {
        status?: number
        code?: string
        details?: Record<string, unknown>
      }

      if (apiError.details) {
        errorDetails = apiError.details
      }
      // ステータスの有無が「サーバーが応答したか」の判別に使われる
      httpStatus = apiError.status
    } else {
      errorMessage = String(error)
    }

    logger.error('[BatchAPI] ❌ Failed to start batch analysis:', {
      spaceId,
      serviceId,
      concurrentExecutionId: request.concurrentExecutionId,
      error: errorMessage,
      status: httpStatus,
      isTimeout,
      details: errorDetails,
    })

    throw new BatchAnalysisError(
      errorMessage,
      isTimeout ? 'BATCH_START_TIMEOUT' : 'BATCH_START_FAILED',
      errorDetails,
      false,
      httpStatus
    )
  }
}

/**
 * 実行中バッチの進捗状況を一括取得する
 * @param spaceId スペースID
 * @param signal AbortSignal
 * @returns 実行中の全バッチの進捗情報
 */
export async function getBatchProgressBulk(
  spaceId: string,
  signal?: AbortSignal
): Promise<BatchProgressBulkResponse> {
  return await apiFetcher.get<BatchProgressBulkResponse>(`/spaces/${spaceId}/batch/progress`, {
    signal,
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  })
}

/**
 * バッチ分析の結果を取得する
 * @param spaceId スペースID
 * @param serviceId サービスID
 * @param concurrentExecutionId 同時実行ID
 * @param signal AbortSignal
 * @returns バッチ分析結果レスポンス
 */
export async function getBatchResults(
  spaceId: string,
  serviceId: string,
  concurrentExecutionId: string,
  signal?: AbortSignal
): Promise<BatchResultsResponse> {
  logger.info('🔧 [BATCH_RESULTS_DEBUG] Frontend getBatchResults called:', {
    spaceId,
    serviceId,
    concurrentExecutionId,
    url: `/spaces/${spaceId}/services/${serviceId}/analyses/batch/${concurrentExecutionId}/results`,
    timestamp: new Date().toISOString(),
    note: 'Investigating if getBatchResults is being called from frontend',
  })

  try {
    let apiResponse: BatchResultsResponse
    try {
      // 🔧 新しい統一レスポンス形式を取得
      apiResponse = await apiFetcher.get<BatchResultsResponse>(
        `/spaces/${spaceId}/services/${serviceId}/analyses/batch/${concurrentExecutionId}/results`,
        { signal }
      )
    } catch (error) {
      // Handle 410 Gone responses gracefully
      if (
        error instanceof Error &&
        (error.message.includes('410') ||
          error.message.toLowerCase().includes('gone') ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).status === 410)
      ) {
        logger.info(
          '[BatchAPI] Batch results resource has been cleaned up (410 Gone) - returning empty results:',
          {
            concurrentExecutionId,
            error: error.message,
            explanation:
              'Batch results have been cleaned up from server. This may happen after some time for completed batches.',
          }
        )

        // Return a minimal results response for 410 Gone
        return {
          success: true,
          data: {
            concurrentExecutionId,
            status: 'SUCCEEDED',
            results: [],
            summary: {
              totalRequested: 0,
              completed: 0,
              failed: 0,
              successRate: 100,
            },
            executionSummary: {
              startedAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              totalExecutionTimeMs: 0,
              averageExecutionTimeMs: 0,
            },
          },
        } as BatchResultsResponse
      }

      // Re-throw other errors for normal error handling
      throw error
    }

    // 🔧 レスポンス構造デバッグ（簡略版）
    // レスポンス構造の検証

    // 🎯 統一レスポンス形式の検証
    if (!apiResponse?.success || !apiResponse?.data) {
      logger.error('[BatchAPI] Invalid unified response structure:', {
        concurrentExecutionId,
        hasSuccess: !!apiResponse?.success,
        hasData: !!apiResponse?.data,
        hasResults: !!apiResponse?.data?.results,
        responseKeys: Object.keys(apiResponse || {}),
        apiResponse: JSON.stringify(apiResponse, null, 2),
      })
      throw new Error(
        `Invalid batch results response: expected unified format with success=true and data object`
      )
    }

    // 必要なフィールドの最終確認
    if (!apiResponse.data.status) {
      logger.error('[BatchAPI] Required fields missing in response:', {
        concurrentExecutionId,
        hasStatus: !!apiResponse.data.status,
        hasResults: Array.isArray(apiResponse.data.results),
        resultsType: typeof apiResponse.data.results,
        response: JSON.stringify(apiResponse, null, 2),
      })
      throw new Error(`Invalid batch results response: missing required status field`)
    }

    // 結果が空の場合の警告ログ（エラーにはしない）
    if (!Array.isArray(apiResponse.data.results) || apiResponse.data.results.length === 0) {
      logger.warn('[BatchAPI] Empty results detected - likely data persistence issue:', {
        concurrentExecutionId,
        status: apiResponse.data.status,
        hasResults: Array.isArray(apiResponse.data.results),
        resultsLength: apiResponse.data.results?.length || 0,
        summaryCompleted: apiResponse.data.summary?.completed || 0,
        summaryTotalRequested: apiResponse.data.summary?.totalRequested || 0,
        note: 'This indicates a backend data persistence problem',
      })
    }

    logger.info('[BatchAPI] Batch results retrieved successfully:', {
      concurrentExecutionId,
      status: apiResponse.data.status,
      totalRequested: apiResponse.data.summary?.totalRequested,
      completed: apiResponse.data.summary?.completed,
      failed: apiResponse.data.summary?.failed,
      successRate: apiResponse.data.summary?.successRate,
      resultsCount: apiResponse.data.results.length,
    })

    return apiResponse
  } catch (error) {
    // AbortErrorの場合は特別な処理
    if (error instanceof Error && error.name === 'AbortError') {
      logger.debug('[BatchAPI] Batch results fetch aborted (likely user cancellation):', {
        spaceId,
        serviceId,
        concurrentExecutionId,
      })
      throw error // AbortErrorはそのまま再スロー
    }

    // 詳細なエラー情報をログ出力
    logger.error('[BatchAPI] ❌ Failed to get batch results:', {
      spaceId,
      serviceId,
      concurrentExecutionId,
      error:
        error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack,
              // APIエラーの場合の追加情報
              status: 'status' in error ? (error as { status?: number }).status : undefined,
              details:
                'details' in error
                  ? (error as { details?: Record<string, unknown> }).details
                  : undefined,
            }
          : error,
    })

    // エラーを再スロー（エラーメッセージはそのまま保持）
    throw error
  }
}

/**
 * Retry failed analyses in a batch
 * @param spaceId - Space ID
 * @param serviceId - Service ID
 * @param batchId - Original batch ID with failed analyses
 * @returns Promise<BatchResultsResponse>
 */
export async function retryBatchAnalysis(
  spaceId: string,
  serviceId: string,
  batchId: string
): Promise<BatchResultsResponse> {
  try {
    logger.info('[BatchAPI] Retrying failed analyses in batch:', {
      spaceId,
      serviceId,
      batchId,
    })

    const response = await apiFetcher.post<BatchResultsResponse>(
      `/spaces/${spaceId}/services/${serviceId}/analyses/batch/${batchId}/retry`,
      {} // Empty body for POST request
    )

    logger.info('[BatchAPI] Retry batch analysis initiated successfully:', {
      batchId,
      newConcurrentExecutionId: response.data?.concurrentExecutionId,
      response: response.data,
    })

    return response
  } catch (error) {
    logger.error('[BatchAPI] Error retrying batch analysis:', {
      batchId,
      error: error instanceof Error ? error.message : String(error),
    })
    throw error
  }
}

/**
 * バッチ分析とフォールバック個別分析を統合したエラーハンドリング
 */
export class BatchAnalysisError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
    public readonly shouldFallback: boolean = false,
    /**
     * サーバーが応答した場合の HTTP ステータス。
     *
     * 開始APIでは、この有無が「サーバーが明示的に拒否したか（確定した失敗）」と
     * 「応答が得られなかったか（開始の有無が未確定）」の判別に使われる。
     * タイムアウト・送信不能では undefined になる。
     */
    public readonly status?: number
  ) {
    super(message)
    this.name = 'BatchAnalysisError'
  }

  static fromResponse(response: BatchErrorResponse): BatchAnalysisError {
    const shouldFallback = [
      'BATCH_API_UNAVAILABLE',
      'BATCH_SIZE_LIMIT_EXCEEDED',
      'BATCH_TIMEOUT',
    ].includes(response.error.code)

    return new BatchAnalysisError(
      response.error.message,
      response.error.code,
      response.error.details,
      shouldFallback
    )
  }

  static fromCancelError(message: string, code?: string): BatchAnalysisError {
    return new BatchAnalysisError(
      message,
      code || 'BATCH_ERROR',
      undefined,
      false // キャンセルエラーはフォールバックしない
    )
  }
}

/**
 * バッチ分析実行をキャンセル
 * @param spaceId - Space ID
 * @param serviceId - Service ID
 * @param batchId - キャンセルするバッチID
 * @returns Promise<キャンセル結果>
 */
export async function cancelBatchAnalysis(
  spaceId: string,
  serviceId: string,
  batchId: string
): Promise<{
  success: boolean
  message: string
  data: {
    batchId: string
    status: 'CANCELLED'
    cancelledAt: string
  }
}> {
  try {
    logger.info('[BatchAPI] Cancelling batch execution:', {
      spaceId,
      serviceId,
      batchId,
    })

    const response = await apiFetcher.post<{
      success: boolean
      message: string
      data: {
        batchId: string
        status: 'CANCELLED'
        cancelledAt: string
      }
    }>(`/spaces/${spaceId}/services/${serviceId}/analyses/batch/${batchId}/cancel`, {})

    logger.info('[BatchAPI] Batch execution cancelled successfully:', {
      batchId: response.data.batchId,
      status: response.data.status,
      cancelledAt: response.data.cancelledAt,
    })

    return response
  } catch (error) {
    logger.error('[BatchAPI] Error cancelling batch execution:', {
      batchId,
      error: error instanceof Error ? error.message : String(error),
    })
    throw BatchAnalysisError.fromCancelError(
      error instanceof Error ? error.message : 'Failed to cancel batch execution',
      'BATCH_CANCEL_FAILED'
    )
  }
}
